import { useEffect, useState } from "react";
import { ApiError, PayrollValidationError, projectQuarterlyForm941 } from "@/api/client";
import type { PayrollEmployee, QuarterlyForm } from "@/api/types";
import { TAX_YEAR } from "@/pages/payroll-fixtures";

export interface UseQuarterlyResult {
  status: "idle" | "loading" | "ready" | "error";
  form: QuarterlyForm | null;
  error: string | null;
}

/**
 * Projects one quarter of a NON-agricultural roster into IRS Form 941.
 *
 * Takes the roster of a specific entity, not the tenant's whole payroll — a 941 built
 * from the farm's agricultural wages would be arithmetically valid and legally wrong.
 * The caller filters by entity; see YearEnd.tsx.
 */
export function useQuarterly(
  employees: PayrollEmployee[] | null,
  quarter: number,
  /**
   * Paydays that actually fall in this quarter, from the server's payroll calendar.
   * NOT a constant: 26 biweekly paydays do not divide into four, so two quarters carry
   * seven and two carry six — which is the honest reason one quarter's return is larger
   * than the next one's. Null until the calendar arrives, which holds the request rather
   * than firing one with an averaged figure that would be quietly wrong.
   */
  payPeriodsInQuarter: number | null,
): UseQuarterlyResult {
  const [status, setStatus] = useState<UseQuarterlyResult["status"]>("idle");
  const [form, setForm] = useState<QuarterlyForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (employees === null || payPeriodsInQuarter === null) {
      setStatus("loading");
      return;
    }
    if (employees.length === 0) {
      setStatus("ready");
      setForm(null);
      return;
    }

    const controller = new AbortController();
    setStatus("loading");
    setError(null);

    projectQuarterlyForm941(
      {
        quarter,
        payPeriodsInQuarter,
        employees: employees.map((employee) => ({
          employeeId: employee.id,
          grossWages: employee.grossWages,
          payPeriodsPerYear: 26,
          taxYear: TAX_YEAR,
          filingStatus: "Single",
          ficaExempt: employee.ficaExemptByLaw,
        })),
      },
      controller.signal,
    )
      .then((result) => {
        setForm(result);
        setStatus("ready");
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          cause instanceof PayrollValidationError
            ? Object.values(cause.errors).flat().join(" ")
            : cause instanceof ApiError
              ? cause.message
              : "Could not project the quarterly return.",
        );
        setStatus("error");
      });

    return () => controller.abort();
  }, [employees, quarter, payPeriodsInQuarter]);

  return { status, form, error };
}
